import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { communicationsApi } from '../../api/communications';
import { useAuth } from '../../contexts/AuthContext';
import TokenHelperBar from './TokenHelperBar';

/**
 * TabTemplates - CRUD UI for message templates
 * RBAC: Management/Admin can create/edit, Supervisor read-only
 */
const TabTemplates = () => {
  const { isAdmin, isManagement } = useAuth();
  const canEdit = isAdmin() || isManagement();

  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLang, setSelectedLang] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    category: 'knowledge_pack_notifications',
    lang: 'en',
    content: '',
    tokens: [],
    active: true
  });

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'knowledge_pack_notifications', label: 'Knowledge Pack Updates' },
    { value: 'emzi_nexus_announcements', label: 'EMZI Nexus Announcements' },
    { value: 'events_promotions', label: 'Events & Promotions' },
    { value: 'internal_notifications', label: 'Internal Notifications' }
  ];

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    filterTemplates();
  }, [templates, searchTerm, selectedCategory, selectedLang]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await communicationsApi.templates.list();
      setTemplates(response.templates || []);
    } catch (error) {
      console.error('Failed to load templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterTemplates = () => {
    let filtered = [...templates];

    if (selectedLang !== 'all') {
      filtered = filtered.filter(t => t.lang === selectedLang);
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(t => t.category === selectedCategory);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(t => 
        t.title.toLowerCase().includes(term) ||
        t.content.toLowerCase().includes(term)
      );
    }

    setFilteredTemplates(filtered);
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setFormData({
      title: '',
      category: 'knowledge_pack_notifications',
      lang: 'en',
      content: '',
      tokens: [],
      active: true
    });
    setShowCreateModal(true);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      title: template.title,
      category: template.category,
      lang: template.lang,
      content: template.content,
      tokens: template.tokens || [],
      active: template.active
    });
    setShowCreateModal(true);
  };

  const handleSave = async () => {
    try {
      // Auto-detect tokens in content
      const tokenRegex = /\{\{([^}]+)\}\}/g;
      const detectedTokens = [];
      let match;
      while ((match = tokenRegex.exec(formData.content)) !== null) {
        const token = `{{${match[1]}}}`;
        if (!detectedTokens.includes(token)) {
          detectedTokens.push(token);
        }
      }

      const dataToSave = {
        ...formData,
        tokens: detectedTokens
      };

      if (editingTemplate) {
        await communicationsApi.templates.update(editingTemplate.id, dataToSave);
      } else {
        await communicationsApi.templates.create(dataToSave);
      }

      setShowCreateModal(false);
      loadTemplates();
    } catch (error) {
      console.error('Failed to save template:', error);
      alert('Failed to save template. Please try again.');
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      await communicationsApi.templates.delete(templateId);
      loadTemplates();
    } catch (error) {
      console.error('Failed to delete template:', error);
      alert('Failed to delete template. Please try again.');
    }
  };

  const insertToken = (token) => {
    setFormData(prev => ({
      ...prev,
      content: prev.content + token
    }));
  };

  return (
    <div 
      className="p-6 overflow-y-auto"
      style={{
        height: '100%',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch'
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Message Templates</h2>
          <p className="text-sm text-gray-600">
            Create and manage bilingual templates for broadcasts and announcements
          </p>
        </div>
        {canEdit && (
          <Button
            onClick={handleCreate}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            + Create Template
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Input
          placeholder="Search templates..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm"
        >
          {categories.map(cat => (
            <option key={cat.value} value={cat.value}>{cat.label}</option>
          ))}
        </select>
        <select
          value={selectedLang}
          onChange={(e) => setSelectedLang(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm"
        >
          <option value="all">All Languages</option>
          <option value="en">English</option>
          <option value="ms">Bahasa Melayu</option>
        </select>
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-sm text-gray-500 mt-4">Loading templates...</p>
        </div>
      ) : filteredTemplates.length === 0 ? (
        <Card className="p-12 text-center">
          <p className="text-gray-500 mb-4">No templates found</p>
          {canEdit && (
            <Button onClick={handleCreate} className="bg-blue-600 hover:bg-blue-700 text-white">
              Create Your First Template
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map(template => (
            <Card key={template.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <CardTitle className="text-lg">{template.title}</CardTitle>
                  <Badge>{template.lang.toUpperCase()}</Badge>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {template.category.replace(/_/g, ' ')}
                </p>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-50 p-3 rounded-lg mb-3 max-h-24 overflow-y-auto">
                  <p className="text-xs text-gray-700 whitespace-pre-wrap">
                    {template.content}
                  </p>
                </div>
                
                {template.tokens && template.tokens.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {template.tokens.map(token => (
                      <Badge key={token} variant="outline" className="text-xs">
                        {token}
                      </Badge>
                    ))}
                  </div>
                )}

                <div className="text-xs text-gray-500 mb-3">
                  <p>Version: {template.version}</p>
                  <p>Updated: {new Date(template.updated_at).toLocaleDateString()}</p>
                </div>

                <div className="flex flex-col gap-2">
                  <Button
                    onClick={() => window.location.href = `/communications?tab=broadcast&templateId=${template.id}`}
                    size="sm"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Use in Broadcast
                  </Button>
                  {canEdit && (
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleEdit(template)}
                        variant="outline"
                        size="sm"
                        className="flex-1"
                      >
                        Edit
                      </Button>
                      {isAdmin() && (
                        <Button
                          onClick={() => handleDelete(template.id)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-4">
                {editingTemplate ? 'Edit Template' : 'Create Template'}
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Title</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="Template title"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      {categories.filter(c => c.value !== 'all').map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Language</label>
                    <select
                      value={formData.lang}
                      onChange={(e) => setFormData({...formData, lang: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="en">English</option>
                      <option value="ms">Bahasa Melayu</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Message Content</label>
                  <TokenHelperBar onInsertToken={insertToken} />
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({...formData, content: e.target.value})}
                    placeholder="Type your message here. Use tokens like {{first_name}} for dynamic content."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm min-h-40"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Tokens will be auto-detected from your content
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({...formData, active: e.target.checked})}
                    id="active"
                  />
                  <label htmlFor="active" className="text-sm">Template is active</label>
                </div>

                <div className="flex gap-3 justify-end pt-4 border-t">
                  <Button
                    onClick={() => setShowCreateModal(false)}
                    variant="outline"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSave}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {editingTemplate ? 'Update' : 'Create'} Template
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TabTemplates;
